import { useCallback, MutableRefObject } from 'react'
import { flushSync } from 'react-dom'
import {
  EditableGridCell,
  GridCellKind,
  GridColumn,
  GridSelection,
  Item
} from '@glideapps/glide-data-grid'
import { UserWithMetadata } from 'Services/apiResponseTypes'
import { CellChange, GridColumnKind, changeKey, originalValueFor } from '../gridModel'

type ValidationFunction = (
  userId: number,
  kind: GridColumnKind,
  fieldName: string,
  value: string | null,
  currentChanges: Map<string, CellChange>
) => string | null

export const useGridEditing = (
  users: UserWithMetadata[],
  columns: GridColumn[],
  columnKinds: GridColumnKind[],
  changesRef: MutableRefObject<Map<string, CellChange>>,
  validationErrorsRef: MutableRefObject<Map<string, string>>,
  validateField: ValidationFunction,
  batchUpdateChanges: (changes: Map<string, CellChange>) => void,
  batchUpdateErrors: (errors: Map<string, string>) => void
) => {
  const revalidateColumn = useCallback(
    (colIndex: number, changes: Map<string, CellChange>, errors: Map<string, string>) => {
      const column = columns[colIndex]
      const columnType = columnKinds[colIndex]
      if (!column || columnType === 'readonly') return

      const field = column.id as string

      users.forEach((user) => {
        const key = changeKey(user.user_id, columnType, field)
        const change = changes.get(key)

        if (columnType === 'userField' && !change) {
          errors.delete(key)
          return
        }

        const value = change ? change.newValue : originalValueFor(user, columnType, field)
        const error = validateField(user.user_id, columnType, field, value, changes)

        if (error) {
          errors.set(key, error)
        } else {
          errors.delete(key)
        }
      })
    },
    [users, columns, columnKinds, validateField]
  )

  const onCellEdited = useCallback(
    (cell: Item, newValue: EditableGridCell) => {
      const [col, row] = cell
      const user = users[row]
      const column = columns[col]
      const columnType = columnKinds[col]

      if (!user || !column || columnType === 'readonly') return

      const field = column.id as string
      const newData = newValue.kind === GridCellKind.Text ? newValue.data : ''
      const originalValue = originalValueFor(user, columnType, field)
      const normalizedNewValue = newData.trim() === '' ? null : newData.trim()

      const key = changeKey(user.user_id, columnType, field)

      const newChanges = new Map(changesRef.current)
      if (normalizedNewValue === originalValue) {
        newChanges.delete(key)
      } else {
        newChanges.set(key, {
          userId: user.user_id,
          kind: columnType,
          field: field,
          newValue: normalizedNewValue
        })
      }

      const newErrors = new Map(validationErrorsRef.current)
      revalidateColumn(col, newChanges, newErrors)

      batchUpdateChanges(newChanges)
      batchUpdateErrors(newErrors)
    },
    [
      users,
      columns,
      columnKinds,
      changesRef,
      validationErrorsRef,
      revalidateColumn,
      batchUpdateChanges,
      batchUpdateErrors
    ]
  )

  const onPaste = (
    target: readonly [number, number],
    values: readonly (readonly string[])[],
    gridSelection: GridSelection
  ): boolean => {
    const [col, row] = target

    const newChanges = new Map(changesRef.current)
    const newErrors = new Map(validationErrorsRef.current)
    const pastedColumns = new Set<number>()

    const isSingleValue = values.length === 1 && values[0].length === 1
    const singleValue = isSingleValue ? values[0][0] : null

    const targetCells = getTargetCells(
      isSingleValue,
      singleValue !== null,
      gridSelection,
      col,
      row,
      values
    )

    targetCells.forEach(([currentCol, currentRow, value]) => {
      if (
        currentRow >= users.length ||
        currentCol >= columns.length ||
        columnKinds[currentCol] === 'readonly'
      ) {
        return
      }

      const user = users[currentRow]
      const column = columns[currentCol]
      const columnType = columnKinds[currentCol]
      const field = column.id as string
      const normalizedValue = value.trim() === '' ? null : value.trim()
      const key = changeKey(user.user_id, columnType, field)
      const originalValue = originalValueFor(user, columnType, field)

      pastedColumns.add(currentCol)

      if (normalizedValue !== originalValue) {
        newChanges.set(key, {
          userId: user.user_id,
          kind: columnType,
          field: field,
          newValue: normalizedValue
        })
      } else {
        newChanges.delete(key)
      }
    })

    pastedColumns.forEach((colIndex) => revalidateColumn(colIndex, newChanges, newErrors))

    flushSync(() => {
      batchUpdateChanges(newChanges)
      batchUpdateErrors(newErrors)
    })
    return true
  }

  return { onCellEdited, onPaste }
}

function getTargetCells(
  isSingleValue: boolean,
  hasSingleValue: boolean,
  gridSelection: GridSelection,
  col: number,
  row: number,
  values: readonly (readonly string[])[]
): Array<[number, number, string]> {
  const targetCells: Array<[number, number, string]> = []

  if (isSingleValue && hasSingleValue && gridSelection.current && gridSelection.current.range) {
    const { x, y, width, height } = gridSelection.current.range
    const singleValue = values[0][0]
    for (let i = 0; i < height; i++) {
      for (let j = 0; j < width; j++) {
        targetCells.push([x + j, y + i, singleValue])
      }
    }
  } else {
    for (let i = 0; i < values.length; i++) {
      const rowData = values[i]
      for (let j = 0; j < rowData.length; j++) {
        targetCells.push([col + j, row + i, rowData[j] || ''])
      }
    }
  }

  return targetCells
}
